import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { Link } from "react-router-dom";

import {
    Home,
    Search,
    Layers,
    Settings,
    LogOut,
    ChevronLeft,
    ChevronRight,
    Target,
    Rss,
    Lock,
    Eye,
    MessageSquare,
    BarChart2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import Tooltip from '@/components/common/ui/Tooltip';
import { useSidebar } from '@/contexts/SidebarContext';
import { useLogout } from '@/hooks/auth/useLogout';
import { useFeatureAccess, FeaturePermissions } from '@/contexts/FeatureAccessContext';
import { useGetOrganizationDetailsQuery } from '@/lib/services/organizationApi';

interface NavigationItem {
    id: string;
    label: string;
    href: string;
    icon: React.ComponentType<{ className?: string }>;
    badge?: string;
    requiredFeature?: keyof FeaturePermissions;
    showWhenRestricted?: boolean;
}

const ALL_NAVIGATION_ITEMS: NavigationItem[] = [
    { id: 'home', label: 'Global Stats', href: '/app', icon: Home },
    {
        id: 'search',
        label: 'Search',
        href: '/app/search',
        icon: Search,
        requiredFeature: 'emailRecords',
        showWhenRestricted: true
    },
    {
        id: 'saved',
        label: 'Saved',
        href: '/app/saved',
        icon: Layers,
        requiredFeature: 'incidentSaving',
        showWhenRestricted: true
    },
    {
        id: 'watchlist',
        label: 'Watchlist',
        href: '/app/watchlist',
        icon: Eye,
        requiredFeature: 'realTimeAlerting',
        showWhenRestricted: true
    },
    {
        id: 'threat-feed',
        label: 'Intelligence Feeds',
        href: '/app/threat-feed',
        icon: Rss,
        requiredFeature: 'threatFeeds',
        showWhenRestricted: true
    },
    {
        id: 'reports',
        label: 'Reports',
        href: '/app/reports',
        icon: BarChart2,
        requiredFeature: 'threatProfiling',
        showWhenRestricted: true
    },
    {
        id: 'threat-profile',
        label: 'Threat Hunting',
        href: '/app/threat-profile',
        icon: Target,
        requiredFeature: 'threatProfiling',
        showWhenRestricted: true
    },
    {
        id: 'telegram',
        label: 'Dark Web Private Forums ',
        href: '/app/forums',
        icon: MessageSquare,
        requiredFeature: 'telegramIntelligence',
        showWhenRestricted: true,
    },
];

const MANAGEMENT_ITEMS: NavigationItem[] = [
    { id: 'settings', label: 'Settings', href: '/app/settings', icon: Settings },
];

// --- LICENSE HELPER TO OVERRIDE OUTDATED CONTEXT FLAG ---
const isLicenseAuthorized = (licenseType?: string | null): boolean => {
    if (!licenseType) return false;
    const clean = licenseType.toLowerCase().replace(/[-_]/g, ' ').trim();
    return (
        clean === 'enterprise' ||
        clean === 'enterprise elite' ||
        clean === 'mssp' ||
        clean.startsWith('enterprise') ||
        clean.startsWith('mssp')
    );
};

const Sidebar: React.FC = () => {
    const location = useLocation();
    const { isCollapsed, toggleSidebar } = useSidebar();
    const { user } = useUser();
    const { logout, isLoading: isLoggingOut } = useLogout();
    const { hasFeature, permissions } = useFeatureAccess();
    const [hoveredItem, setHoveredItem] = useState<string | null>(null);

    // Fetch exact license data from the backend API
    const { data: orgData } = useGetOrganizationDetailsQuery();

    // Extract raw license type safely from the API response fallback to context
    const rawLicenseType = 
        orgData?.data?.license?.type || 
        (orgData as any)?.license?.type ||
        (permissions as any)?.licenseType || 
        (permissions as any)?.license?.type ||
        (permissions as any)?.type;

    const isActive = (href: string) => {
        if (href === '/app') {
            return location.pathname === '/app' || location.pathname === '/app/';
        }
        return location.pathname.startsWith(href);
    };

    const getInitials = (name: string) => {
        return name
            .split(' ')
            .map(part => part[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const handleLogout = async () => {
        await logout();
    };

    const getFilteredNavItems = (items: NavigationItem[]) => {
        return items.filter(item => {
            if (!item.requiredFeature) return true;
            if (item.showWhenRestricted) return true;
            
            if (item.id === 'telegram') {
                return isLicenseAuthorized(rawLicenseType);
            }
            return hasFeature(item.requiredFeature);
        });
    };

    const getTooltipText = (item: NavigationItem): string => {
        let isRestricted = item.requiredFeature && !hasFeature(item.requiredFeature);
        
        // Override check for Telegram forums
        if (item.id === 'telegram') {
            isRestricted = !isLicenseAuthorized(rawLicenseType);
        }

        const hasValidLicense = permissions.hasValidLicense;

        if (isRestricted) {
            return `${item.label} - Upgrade Required`;
        } else if (!hasValidLicense) {
            return `${item.label} - License Expired`;
        } else if (item.badge) {
            return `${item.label} (${item.badge})`;
        }
        return item.label;
    };

    const renderNavItem = (item: NavigationItem) => {
        const Icon = item.icon;
        const active = isActive(item.href);
        
        let isRestricted = item.requiredFeature && !hasFeature(item.requiredFeature);
        
        // Override check for Telegram forums
        if (item.id === 'telegram') {
            isRestricted = !isLicenseAuthorized(rawLicenseType);
        }

        const hasValidLicense = permissions.hasValidLicense;

        if (permissions.isLoading) return null;

        const tooltipText = getTooltipText(item);
        const showTooltip = isCollapsed && hoveredItem === item.id;

        return (
            <div key={item.id} className="overflow-visible px-3">
                <Tooltip content={tooltipText} show={showTooltip} position="right">
                    {isRestricted || !hasValidLicense ? (
                        <button
                            disabled
                            onMouseEnter={() => isCollapsed && setHoveredItem(item.id)}
                            onMouseLeave={() => setHoveredItem(null)}
                            className={cn(
                                "flex items-center rounded-xl text-xs font-medium transition-all duration-300 group relative cursor-not-allowed opacity-40 w-full border border-transparent",
                                isCollapsed ? "justify-center px-3 py-3" : "justify-between px-3 py-2.5",
                                "text-slate-500"
                            )}
                        >
                            <div className={cn("flex items-center", isCollapsed ? "justify-center" : "space-x-3")}>
                                <div className="relative">
                                    <Icon className="w-4 h-4 transition-colors flex-shrink-0" />
                                    <Lock className="w-3 h-3 absolute -top-1 -right-2 text-slate-600" />
                                </div>
                                {!isCollapsed && (
                                    <span className="flex items-center space-x-2 tracking-wide">
                                        <span>{item.label}</span>
                                    </span>
                                )}
                            </div>
                        </button>
                    ) : (
                        <NavLink
                            to={item.href}
                            onMouseEnter={() => isCollapsed && setHoveredItem(item.id)}
                            onMouseLeave={() => setHoveredItem(null)}
                            className={cn(
                                "flex items-center rounded-lg text-xs font-medium transition-all duration-300 group relative w-full overflow-hidden",
                                isCollapsed ? "justify-center px-3 py-3" : "justify-between px-3 py-2.5",
                                active
                                    ? "bg-gradient-to-r from-[#50A970]/20 to-transparent backdrop-blur-md border border-[#50A970]/30 text-white shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)]"
                                    : "text-slate-400 border border-transparent hover:text-white hover:bg-white/[0.02] hover:border-white/[0.05] hover:translate-x-1"
                            )}
                        >
                            {active && (
                                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-[#50A970] rounded-r-full shadow-[0_0_12px_rgba(80,169,112,0.6)]" />
                            )}
                            <div className={cn("flex items-center relative z-10", isCollapsed ? "justify-center" : "space-x-3")}>
                                <Icon className={cn(
                                    "w-4 h-4 flex-shrink-0 transition-transform duration-300",
                                    active ? "text-[#50A970] drop-shadow-[0_0_5px_rgba(80,169,112,0.5)]" : "text-slate-500 group-hover:text-slate-300"
                                )} />
                                {!isCollapsed && <span className="tracking-wide">{item.label}</span>}
                            </div>

                            {!isCollapsed && item.badge && (
                                <span className="bg-[#50A970] text-[#0A0B0E] text-[10px] px-2 py-0.5 rounded font-bold shadow-[0_0_8px_rgba(80,169,112,0.4)] tracking-wider relative z-10">
                                    {item.badge}
                                </span>
                            )}

                            {isCollapsed && item.badge && (
                                <span className="absolute -top-1 -right-1 bg-[#50A970] text-[#0A0B0E] text-[9px] w-3.5 h-3.5 rounded-full font-bold flex items-center justify-center shadow-[0_0_8px_rgba(80,169,112,0.4)] z-10">
                                    {item.badge}
                                </span>
                            )}
                        </NavLink>
                    )}
                </Tooltip>
            </div>
        );
    };

    const renderManagementItem = (item: NavigationItem) => {
        const Icon = item.icon;
        const active = isActive(item.href);
        const showTooltip = isCollapsed && hoveredItem === item.id;

        return (
            <div key={item.id} className="px-3">
                <Tooltip content={item.label} show={showTooltip} position="right">
                    <NavLink
                        to={item.href}
                        onMouseEnter={() => isCollapsed && setHoveredItem(item.id)}
                        onMouseLeave={() => setHoveredItem(null)}
                        className={cn(
                            "flex items-center rounded-lg text-xs font-medium transition-all duration-300 group relative w-full overflow-hidden",
                            isCollapsed ? "justify-center px-3 py-3" : "justify-between px-3 py-2.5",
                            active
                                ? "bg-gradient-to-r from-[#50A970]/20 to-transparent backdrop-blur-md border border-[#50A970]/30 text-white shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)]"
                                : "text-slate-400 border border-transparent hover:text-white hover:bg-white/[0.02] hover:border-white/[0.05] hover:translate-x-1"
                        )}
                    >
                        {active && (
                            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-[#50A970] rounded-r-full shadow-[0_0_12px_rgba(80,169,112,0.6)]" />
                        )}
                        <div className={cn("flex items-center relative z-10", isCollapsed ? "justify-center" : "space-x-3")}>
                            <Icon className={cn(
                                "w-4 h-4 flex-shrink-0 transition-all duration-300",
                                active ? "text-[#50A970] drop-shadow-[0_0_5px_rgba(80,169,112,0.5)]" : "text-slate-500 group-hover:text-white"
                            )} />
                            {!isCollapsed && <span className="tracking-wide">{item.label}</span>}
                        </div>
                    </NavLink>
                </Tooltip>
            </div>
        );
    };

    return (
        <aside className={cn(
            "relative z-40 flex flex-col h-full transition-all duration-300 ease-in-out hidden lg:flex bg-[#0A0B0E] border-r border-[#1A1D24]",
            isCollapsed ? "w-20" : "w-64"
        )}>
            {/* --- EXACT FIGMA LINEAR GRADIENT GLOW --- */}
            <div className="absolute top-0 right-0 w-[1px] h-full bg-gradient-to-b from-[#1A1D24] from-[0%] via-[#50A970] via-[54%] to-[#1A1D24] to-[100%] shadow-[0_0_15px_5px_rgba(80,169,112,0.25)] pointer-events-none z-50"></div>

            {/* Logo Section */}
            <div className={cn(
                "h-24 border-b border-[#1A1D24] flex items-center transition-all duration-300",
                isCollapsed ? "justify-center" : "px-6"
            )}>
                <Tooltip content="ThreatInformant" show={isCollapsed && hoveredItem === 'logo'} position="right">
                    <Link
                        to="/"
                        className={cn("flex items-center group w-full", isCollapsed ? "justify-center" : "gap-3")}
                        onMouseEnter={() => isCollapsed && setHoveredItem('logo')}
                        onMouseLeave={() => setHoveredItem(null)}
                    >
                        {isCollapsed ? (
                            <div className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-[#50A970]/10 to-transparent border border-[#50A970]/20 flex-shrink-0 group-hover:border-[#50A970]/40 transition-colors duration-300">
                                <img
                                    src="/image/logo.png"
                                    alt="ThreatInformant"
                                    className="object-contain h-8 w-auto"
                                />
                            </div>
                        ) : (
                            <div className="flex items-center w-full transition-all duration-300">
                                <img
                                    src="/image/logo-2.png"
                                    alt="ThreatInformant"
                                    className="object-contain h-24 w-auto"
                                />
                            </div>
                        )}
                    </Link>
                </Tooltip>
            </div>

            {/* Collapse Toggle Button */}
            <button
                onClick={toggleSidebar}
                className="absolute -right-3 top-10 w-6 h-6 bg-[#0A0B0E] border border-[#1A1D24] rounded-full flex items-center justify-center text-slate-500 hover:text-white hover:border-[#50A970] hover:shadow-[0_0_10px_rgba(80,169,112,0.3)] transition-all z-50"
            >
                {isCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
            </button>

            {/* Navigation */}
            <nav className="flex-1 py-6 overflow-y-auto overflow-x-hidden custom-scrollbar space-y-1">
                <div className={cn(
                    "text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-3",
                    isCollapsed ? "text-center" : "px-6"
                )}>
                    {isCollapsed ? '///' : 'Main'}
                </div>
                {getFilteredNavItems(ALL_NAVIGATION_ITEMS).map(renderNavItem)}
            </nav>

            {/* Management & System Section */}
            <div className="border-t border-[#1A1D24]">

                <div className="py-4">
                    {!isCollapsed && (
                        <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest px-6 mb-3">
                            System
                        </div>
                    )}

                    <div className="space-y-1">
                        {MANAGEMENT_ITEMS.map(renderManagementItem)}

                        {/* Logout Button */}
                        <div className="px-3 mt-1">
                            <Tooltip content="Sign Out" show={isCollapsed && hoveredItem === 'logout'} position="right">
                                <button
                                    onMouseEnter={() => isCollapsed && setHoveredItem('logout')}
                                    onMouseLeave={() => setHoveredItem(null)}
                                    onClick={handleLogout}
                                    disabled={isLoggingOut}
                                    className={cn(
                                        "w-full flex items-center rounded-lg text-xs font-medium transition-all duration-300 group",
                                        "text-slate-400 border border-transparent hover:text-[#FF3333] hover:bg-[#FF3333]/10 hover:border-[#FF3333]/20",
                                        "disabled:opacity-50 disabled:cursor-not-allowed",
                                        isCollapsed ? "justify-center px-3 py-3" : "px-3 py-2.5 space-x-3"
                                    )}
                                >
                                    {isLoggingOut ? (
                                        <div className="w-4 h-4 animate-spin rounded-full border-2 border-[#FF3333]/30 border-t-[#FF3333] flex-shrink-0" />
                                    ) : (
                                        <LogOut className="w-4 h-4 flex-shrink-0 transition-transform group-hover:-translate-x-1" />
                                    )}
                                    {!isCollapsed && <span className="tracking-wide">{isLoggingOut ? 'Terminating...' : 'Sign Out'}</span>}
                                </button>
                            </Tooltip>
                        </div>
                    </div>
                </div>

                {/* User Profile Footer */}
                {user && (
                    <div className="p-4 border-t border-[#1A1D24] bg-[#0A0B0E]">
                        <Tooltip content={user.fullName || 'User Profile'} show={isCollapsed && hoveredItem === 'user'} position="right">
                            <div
                                className={cn(
                                    "flex items-center rounded-xl transition-all duration-300 cursor-pointer group",
                                    isCollapsed ? "justify-center" : "px-3 py-2 hover:bg-[#161A23] border border-transparent hover:border-[#1A1D24]"
                                )}
                                onMouseEnter={() => isCollapsed && setHoveredItem('user')}
                                onMouseLeave={() => setHoveredItem(null)}
                            >
                                <div className="relative">
                                    {user.imageUrl ? (
                                        <img
                                            src={user.imageUrl}
                                            alt={user.fullName || "User"}
                                            className="w-8 h-8 rounded-lg object-cover border border-[#1A1D24] group-hover:border-[#50A970]/50 transition-colors shadow-lg"
                                        />
                                    ) : (
                                        <div className="w-8 h-8 rounded-lg bg-[#161A23] border border-[#1A1D24] flex items-center justify-center text-white text-[10px] font-bold group-hover:border-[#50A970]/50 transition-colors shadow-lg">
                                            {user.fullName ? getInitials(user.fullName) : 'OP'}
                                        </div>
                                    )}
                                    <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-[#50A970] border-2 border-[#0A0B0E] rounded-full" />
                                </div>

                                {!isCollapsed && (
                                    <div className="ml-3 flex-1 min-w-0">
                                        <p className="text-xs font-bold text-white truncate group-hover:text-[#50A970] transition-colors">
                                            {user.fullName || "Operator"}
                                        </p>
                                        <p className="text-[10px] text-slate-500 font-mono truncate">
                                            {user.primaryEmailAddress?.emailAddress}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </Tooltip>
                    </div>
                )}
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
                .custom-scrollbar::-webkit-scrollbar { width: 4px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #1A1D24; border-radius: 4px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #50A970; }
            `}} />
        </aside>
    );
};

export default Sidebar;